/**
 * Lightweight analytics for Measured Livestock.
 *
 * Sends page-view + conversion events to:
 *   1. Our own backend (/api/analytics/track) for the in-Admin counter.
 *   2. Google Analytics 4 (if REACT_APP_GA4_ID is configured).
 *
 * Safe to call before GA loads — events are queued via the standard
 * dataLayer/gtag pattern.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GA4_ID = process.env.REACT_APP_GA4_ID || '';

const SESSION_KEY = 'ml_session_id';

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/** Inject the GA4 script tag once, if a measurement ID is configured. */
let ga4Initialised = false;
export function initGA4() {
  if (ga4Initialised || !GA4_ID) return;
  ga4Initialised = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA4_ID, { send_page_view: false }); // we send manually on route change
}

/** Track a page view — fires both backend + GA4. */
export function trackPageView(path) {
  const session_id = getSessionId();

  // Backend
  axios.post(`${BACKEND_URL}/api/analytics/track`, {
    path: path || window.location.pathname,
    session_id,
    referrer: document.referrer || '',
    event: 'page_view',
  }).catch(() => { /* silent — never break the UX */ });

  // GA4
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path || window.location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}

/** Track a conversion-style event (reg_complete, quote_calculated, booking_submitted). */
export function trackEvent(name, extra = {}) {
  const session_id = getSessionId();

  axios.post(`${BACKEND_URL}/api/analytics/track`, {
    path: window.location.pathname,
    session_id,
    event: name,
  }).catch(() => { /* silent */ });

  if (window.gtag) {
    window.gtag('event', name, extra);
  }
}
